import React, { useContext, useState, useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { ExperienceContext } from '@thd-nucleus/experience-context';
import { Typography, Alert } from '@one-thd/sui-atomic-components';
import {
  CustomerAccountNavigation,
  useAccountCrumbs,
} from '@thd-olt-component-react/customer-account-navigation';
import { MilitaryVerification } from '@thd-olt-component-react/military-verification';
import { ErrorBoundary } from '@thd-olt-component-react/error-boundary';
import { QueryProvider, extend } from '@thd-nucleus/data-sources';
import { Breadcrumbs } from '@thd-olt-component-react/breadcrumbs';
import FAQs from '../components/common/FAQs';
import ProgramSectionContainer from '../components/ProgramSection/ProgramSectionContainer';
import { redirectToSignIn } from '../utils/utils';
import { ROUTES } from '../constants/constants';
import { MD_NOT_ENROLLED } from '../constants/enrollment-status';
import { Verification } from '../components/Verification';
import { useMilitaryDiscountDetails } from '../hooks/useMilitaryDiscountDetails';
import { useIdmeEnabled } from '../hooks/useIdmeEnabled';
import { militaryDiscountDetailsDataModel } from '../api/b2b/graphql/militaryDiscountDetails.dataModel';
import { isAuthFailure } from '../api/authError';
import { mapCrumbsToStaticCrumbs } from '../utils/breadcrumbs';
import { track, lifeCycleTrack } from '../analytics/analytics';
import { PAGE_LOAD, GET_STARTED_LINK } from '../constants/analytics';
import {
  GENERAL_ERROR_MSG,
  REDIRECT_ERROR_MSG,
} from '../constants/alert-messages';
import { SectionSkeleton } from '../components/common/Loaders/Loaders';
import { MilitaryVerificationErrorProvider } from '../contexts/MilitaryVerificationErrorContext';
import { MilitaryVerificationErrorBanner } from '../components/common/MilitaryVerificationErrorBanner';
import {
  MilitaryVerificationSuccessProvider,
  useMilitaryVerificationSuccess,
} from '../contexts/MilitaryVerificationSuccessContext';
import { MilitaryVerificationSuccessToast } from '../components/common/MilitaryVerificationSuccessToast';

const crumbs = [
  {
    label: ROUTES.landing.label,
    path: ROUTES.landing.url,
  },
];

export const LandingPage = ({ customerAccountId, userId }) => {
  return (
    <MilitaryVerificationErrorProvider>
      <MilitaryVerificationSuccessProvider>
        <QueryProvider cacheKey="military-discount-landing-page">
          <LandingPageContent
            customerAccountId={customerAccountId}
            userId={userId}
          />
        </QueryProvider>
      </MilitaryVerificationSuccessProvider>
    </MilitaryVerificationErrorProvider>
  );
};

LandingPage.propTypes = {
  customerAccountId: PropTypes.string,
  userId: PropTypes.string,
};
LandingPage.defaultProps = {};
LandingPage.displayName = 'LandingPage';

const LandingPageContent = ({ customerAccountId, userId }) => {
  const { channel, isConsumerApp } = useContext(ExperienceContext);
  const isMobile = channel === 'mobile';
  /* The program section reads this flag too, to choose which vendor's entry
  /* point to offer. It is read again here because the flag decides something the
  /* section cannot: whether this page may replace itself with the previous
  /* vendor's screen and never render the section at all. */
  const isIdmeEnabled = useIdmeEnabled();
  const [verifyRequested, setVerifyRequested] = useState(false);
  const staticCrumbs = mapCrumbsToStaticCrumbs(useAccountCrumbs(crumbs));
  const [isSpouse, setIsSpouse] = useState(false);
  const { verifiedProfile } = useMilitaryVerificationSuccess();

  const {
    details,
    loading: isLoading,
    error,
    hasIdentifiers,
    refetch,
  } = useMilitaryDiscountDetails();

  /* The enrollment query is the system of record, but it lags: enrollment is a
  /* Loyalty concept updated asynchronously off the ID.me write via CDC, so a
  /* customer who verified seconds ago still reads as unenrolled here and
  /* refetching would not change that. A verification this page saw for itself is
  /* the only thing that covers the gap, so it counts alongside the query rather
  /* than waiting on it.
  /*
  /* Resolved once, here, and handed to everything that renders off it - the
  /* program section and the FAQs both key on enrollment, and a page that worked
  /* this out in two places would end up congratulating the customer above a set
  /* of instructions for enrolling. */
  const isEnrolled = details?.isEnrolled === true || !!verifiedProfile;

  /* Two ways a customer ends up at sign-in rather than on this page: no session
  /* to read identifiers out of, and a session the BFF turned down. The REST call
  /* said so with a 401 or a 403; a GraphQL request resolves 200 whatever the
  /* resolver did, so the second case is read off the error - see api/authError.js. */
  const isUnauthenticated = !hasIdentifiers || isAuthFailure(error);

  /* Derived rather than stored, unlike the state this replaces: both flavours
  /* are already decided by the query's own error, and a stored copy would have
  /* to be cleared on a successful refetch as well as set on a failed one. Kept
  /* in the shape the sections below already read. */
  const apiError = useMemo(() => {
    if (isUnauthenticated) {
      return { showRedirectAlert: true };
    }
    if (error) {
      return { generalError: true };
    }
    return false;
  }, [isUnauthenticated, error]);

  /* The redirect carries an in-flight ID.me verification with it, so a customer
  /* whose session lapsed while they were away does not have to start over - see
  /* signInRedirectUrl in utils. */
  useEffect(() => {
    if (isUnauthenticated) {
      redirectToSignIn();
    }
  }, [isUnauthenticated]);

  /* An application the customer opened with the previous vendor and never
  /* finished. Reported as an identifier sitting beside NOT_ENROLLED - there is
  /* no other field that says so, and the status has to be read rather than
  /* `isEnrolled`, because the distinction that matters is NOT_ENROLLED from
  /* PENDING and a test for `'Y'` cannot draw it. */
  const hasUnfinishedPreviousApplication =
    !!details?.verificationId && details.enrollmentStatus === MD_NOT_ENROLLED;

  /* Which of the page's two views is shown.
  /*
  /* Gated on the flag, which it was not: an unfinished previous application sent
  /* every customer to the retired screen whether ID.me was switched on or not,
  /* so a customer returning from ID.me was handed that screen instead of their
  /* result - discarding a verification they had just completed, with no error
  /* and nothing to say it happened (MYA2-437). The program section is what
  /* settles a return, and it never rendered.
  /*
  /* Derived every render rather than latched into state, because the flag is now
  /* part of the answer and does not have to resolve before the query does. A
  /* boolean set when enrollment arrived would strand a customer on the retired
  /* screen for the rest of the session if the config answered a moment later,
  /* since nothing sets it back. Same reason `isEnrolled` is resolved this way. */
  const showIframe =
    !isIdmeEnabled && (verifyRequested || hasUnfinishedPreviousApplication);

  const header = showIframe ? 'Verification' : 'Military Discount Benefit';

  /* Fired once, when the query first comes back, and deliberately not on the
  /* refetch the verify button makes below - this is the page-load event.
  /* `isEnrolled` here is the BFF's answer on its own rather than the page's
  /* reconciled one, because a verification this page saw for itself is a
  /* different event. */
  const hasTrackedPageLoad = useRef(false);
  useEffect(() => {
    if (hasTrackedPageLoad.current || !details) {
      return;
    }
    hasTrackedPageLoad.current = true;
    lifeCycleTrack(PAGE_LOAD, {
      isEnrolled: details.isEnrolled === true,
      hasMaxDiscount: details.isMilitaryDiscountCapped === true,
    });
  }, [details]);

  /* Re-asks before handing the customer off to the third-party verification,
  /* purely as a session check: a session that lapsed while they read the page
  /* would lose the application they are about to start. A lapsed one is sent to
  /* sign-in by the effect above, off the same error.
  /*
  /* Any other failure is ignored on purpose. Enrollment status has no bearing on
  /* whether a verification can be started, so refusing to act on the click would
  /* leave the customer on the benefit page with nothing to show for it. A failed
  /* re-ask does empty the response out from under the view, which is why the
  /* view reads the identifier defensively rather than assuming one - see
  /* components/Verification. */
  const verifyOnClick = async (spouseFlag) => {
    track(GET_STARTED_LINK);
    setIsSpouse(spouseFlag);
    await refetch().catch(() => {});
    setVerifyRequested(true);
  };

  const pageContent = showIframe ? (
    <Verification apiResponse={details} isSpouse={isSpouse} />
  ) : (
    <>
      <div className="sui-grid sui-gap-3 sui-py-3">
        {!isConsumerApp && (
          <ErrorBoundary name="breadcrumbs">
            <Breadcrumbs staticCrumbs={staticCrumbs} />
          </ErrorBoundary>
        )}
        {!isMobile && (
          <Typography
            data-testid="page-header"
            weight="display"
            variant="h1"
            uppercase
          >
            {header}
          </Typography>
        )}
      </div>
      <div className="sui-grid sui-grid-cols-12 sui-gap-5 sui-py-3">
        <div
          className="sui-col-span-12 lg:sui-col-span-3"
          data-testid="left-nav"
        >
          <ErrorBoundary name="customer-account-navigation">
            <CustomerAccountNavigation />
          </ErrorBoundary>
        </div>

        <div className="sui-col-span-12 lg:sui-col-span-9">
          {/* Suppressed on a general enrollment-status failure so it doesn't stack with GENERAL_ERROR_MSG below. */}
          {!apiError?.generalError && <MilitaryVerificationErrorBanner />}
          {apiError?.generalError ? (
            <Alert status="error">{GENERAL_ERROR_MSG}</Alert>
          ) : (
            <>
              <div className="sui-mb-4">
                <Typography variant="h2">
                  Our Thanks for Your Service
                </Typography>
                <Typography>
                  Receive{' '}
                  <Typography component="span" weight="bold">
                    10% off
                  </Typography>{' '}
                  eligible purchases* - every day, year round - if you&apos;re
                  an active service member, veteran or military spouse.
                </Typography>
              </div>
              {isLoading && <SectionSkeleton section="program" />}
              <ErrorBoundary name="program-section-container">
                <ProgramSectionContainer
                  apiResponse={details}
                  apiError={apiError}
                  isEnrolled={isEnrolled}
                  verifyOnClick={verifyOnClick}
                  customerAccountId={customerAccountId}
                  userId={userId}
                />
              </ErrorBoundary>
              <div className="sui-mb-4 sui-mt-16">
                <Typography variant="h2">FAQs</Typography>
              </div>
              {isLoading && <SectionSkeleton section="FAQ" />}
              <ErrorBoundary name="faqs">
                <FAQs apiResponse={details} isEnrolled={isEnrolled} />
              </ErrorBoundary>
            </>
          )}
        </div>
      </div>
    </>
  );

  return (
    <div className="container">
      <div className="grid isBound">
        <div className="col__12-12 u--fix-960">
          {apiError?.showRedirectAlert ? (
            <div className="sui-mt-8">
              <Alert status="info">{REDIRECT_ERROR_MSG}</Alert>
            </div>
          ) : (
            pageContent
          )}
        </div>
      </div>
      {/* Deliberately outside the view switch above, and outside the column the
          page renders into: the toast has to survive the re-render that takes
          the verify button off the page, and it pins itself to the bottom of the
          viewport rather than to whatever contains it. */}
      <MilitaryVerificationSuccessToast />
    </div>
  );
};

LandingPageContent.propTypes = {
  customerAccountId: PropTypes.string,
  userId: PropTypes.string,
};

LandingPageContent.dataModel = extend(
  CustomerAccountNavigation,
  Breadcrumbs,
  MilitaryVerification,
  militaryDiscountDetailsDataModel,
);

LandingPageContent.displayName = 'LandingPageContent';
